import { RPUserStatus } from './User'
import { Image, LanguageCode, PickerItem, MapAreaLevel } from './Constants'
import { Jurisdiction, JurisdictionChunkProperties } from './Organization'
import { PrepCheckQuestionType } from './PrepChecks'
import { BBox } from 'geojson'

export interface GetAdoptionStatsDTO {
    stat?: CommunityAdoptionStat
    frequency: CommunityAdoptionInterval
    zips?: string[]
}

export interface AdoptionStatsDTO extends GetAdoptionStatsDTO {
    count: number
}

export interface InsightPicker {
    selected_option?: string
    options: PickerItem<string>[]
}

export interface InsightPickers {
    [key: string]: InsightPicker
}

export interface InsightConfig {
    pickers: InsightPickers
    bbox?: BBox
    zoom?: number
    visible_tiles?: string[]
}

export type InsightGridConfig = {
    [key: string]: InsightConfig
}

export interface InsightCta {
    label: string
    href: string
}

export const VISITOR_INSIGHTS_SUFFIX = '--visitors'
export enum InsightSortMethod {
    PAIR_RESIDENTS_VISITORS = 'pair-residents-visitors',
    SEGMENT_RESIDENTS_VISITORS = 'segment-residents-visitors',
}

export interface InsightQueryBase {
    type: InsightQueryType
    grid_config: InsightGridConfig
    start_date?: Date
    end_date?: Date
    hazards?: number[]
    prep_checks?: number[]
    zips?: string[]
    counties?: string[]
    states?: string[]
    question_id?: number
    language?: LanguageCode
    sort_method?: InsightSortMethod
    insight_ids: string[]
}

export interface InsightQuery extends InsightQueryBase {
    id: number
    rp_user_id?: number
    date_last_used: Date
    area?: Jurisdiction
}

export interface InsightQueryDTO extends InsightQuery {
    user_name: string
    user_profile_picture_uri?: string
    user_status: RPUserStatus
    hazard_names?: string[]
    prep_check_titles?: string[]
    description: string
}

export interface InsightsDTO extends InsightQueryDTO {
    insights: Insight[]
    quick_stat_row_title?: string
}

export interface SingleInsightDTO {
    insight: Insight
    config: InsightConfig
}

export interface TimePointOfInterestDTO {
    timestamp: Date
    label: string
}

export type InsightDemographicMode = 'residents' | 'visitors'

export interface InsightBase {
    placement_id: string
    headline?: string
    subline?: string
    explainer?: string
    date_updated: Date
    show_if_no_data?: boolean
    ctas?: InsightCta[]
    prev?: string
    next?: string
    demographic_mode?: InsightDemographicMode
}

export interface CounterInsightItem {
    id: string
    label: string
    counterText: string
    counter: number
    viewConfig?: {
        backgroundColor: string
        icon: Image
    }
}

export interface SingleBarGraphItem {
    id: string
    value: number
    label: string
    color: string
}

// NOTE: This is for one group ACROSS the graph, so there should be one value in the values list for each label in the labels list on the insight
// e.g. all the "prepare" open counts for each hazard
export interface MultiBarGraphGroup {
    id: string
    values: number[]
    name: string
    color: string
    togglable?: boolean
}

export interface LineGraphInsightItem {
    id: string
    values: number[]
    label: string
    color: string
}

export interface MapInsightTile {
    id: string
    label: string
    name: string
    value: number
    subcopy?: string
    fillColor: string
    lightening: number
}

export interface DonutChartItem extends SingleBarGraphItem {}
export interface LineFillItem extends SingleBarGraphItem {
    valueLabel: string
}

export interface SurveyAnswerInsightItem
    extends Omit<SingleBarGraphInsight, 'type'>,
        Omit<MapInsight, 'type' | 'legend'> {
    answer: string
    weight: number
    map_legend: MapLegendItem[]
}

export interface LineGraphPoint<T = string> {
    timestamp: Date
    label: T
}

export interface ShareableViewConfig {
    insights: string[]
    invitees: string[]
    start_date?: Date
    end_date?: Date
    hazards?: number[]
    area?: Jurisdiction
}

export interface ChartAxes {
    x: string
    y: string
}

export type Insight =
    | SingleCounterInsight
    | MultiCounterInsight
    | SingleBarGraphInsight
    | MultiBarGraphInsight
    | LineGraphInsight
    | MapInsight
    | DonutChartInsight
    | LineFillInsight
    | SurveyAnswerInsight
    | QuickStatInsight

export interface TimePointOfInterest extends TimePointOfInterestDTO {
    id: number
}

export interface SingleCounterInsight extends InsightBase {
    type: 'single-counter'
    counter: CounterInsightItem[]
}

export interface MultiCounterInsight extends InsightBase {
    type: 'multi-counter'
    counters: CounterInsightItem[]
}

export interface SingleBarGraphInsight extends InsightBase {
    type: 'bar'
    bars: SingleBarGraphItem[]
    axes: ChartAxes
    orientation: 'horizontal' | 'vertical'
}

export interface MultiBarGraphInsight extends InsightBase {
    type: 'bar'
    groups: MultiBarGraphGroup[]
    labels: string[]
    axes: ChartAxes
    orientation: 'horizontal' | 'vertical'
    stacked: boolean
}

export interface LineGraphInsight extends InsightBase {
    type: 'line'
    lines: LineGraphInsightItem[]
    points: LineGraphPoint<number>[]
    axes: ChartAxes
}

export interface MapInsight extends InsightBase {
    type: 'map'
    map_config: MapInsightTile[]
    legend: MapLegendItem[]
    bbox?: BBox
}

export interface DonutChartInsight extends InsightBase {
    type: 'donut'
    chunks: DonutChartItem[]
}

export interface LineFillInsight extends InsightBase {
    type: 'line-fill'
    lines: LineFillItem[]
}

export interface SurveyAnswerInsight extends InsightBase {
    type: 'survey-answer'
    question_id: number
    question_type: PrepCheckQuestionType
    question: string
    subcopy: string
    answers: SurveyAnswerInsightItem[]
}

export interface QuickStatInsight extends InsightBase {
    type: 'quick-stat'
    icon: Image
    color: string
    value: number | string
    unit?: string
    href?: string
}

export enum CommunityAdoptionStat {
    REGISTERED_PROFILES = 'registered-profiles',
    TOTAL_USERS = 'total-users',
    TOTAL_OPENS = 'total-opens',
    PREP_CHECK_USERS = 'prep-check-users',
}

export enum CommunityAdoptionInterval {
    TODAY = 'today',
    WEEK = 'week',
    MONTH = 'month',
    QUARTER = 'quarter',
    YEAR = 'year',
    ALL_TIME = 'all-time',
}

export enum ExportFormat {
    PDF = 'pdf',
    PNG = 'png',
    CSV = 'csv',
}

export enum InsightQueryType {
    CORE = 'core',
    HAZARDS = 'hazards',
    PREP_CHECKS = 'prep-checks',
    PREP_CHECK_QUESTION = 'prep-check-question',
}

export interface InsightQueryHistory {
    yours: InsightQueryDTO[]
    others?: InsightQueryDTO[]
    page: number
    num_pages: number
}

export interface MapColorTheme {
    label: string
    colors: string[]
}

export interface MapLegendItem {
    color: string
    label: string
}

export interface MapTileProperties extends JurisdictionChunkProperties {
    content: string
}

export enum InsightId {
    // Core Insights
    HAZARD_VIEWS = 'hazard-views',
    APP_USAGE = 'app-usage',
    AVG_PREP_CHECK_QUESTION_SCORES = 'avg-prep-check-question-scores',
    AVG_PREP_CHECK_QUESTION_SCORES__VISITORS = 'avg-prep-check-question-scores--visitors',
    ACTIVITY_BY_AREA = 'activity-by-area',
    REGISTERED_USERS = 'registered-users',
    NEW_USERS = 'new-users',
    // Hazard Guide Insights
    GUIDE_VIEWS = 'guide-views',
    MOST_USED_LANGUAGES = 'most-used-languages',
    GUIDE_VIEWS_BY_AREA = 'guide-views-by-area',
    MOST_VIEWED_HAZARDS = 'most-viewed-hazards',
    // Hazard Insights
    CONTENT_USAGE = 'content-usage',
    ACTION_PHASE_USAGE = 'action-phase-usage',
    HAZARD_LANGUAGES = 'hazard-languages',
    HAZARD_USAGE_BY_AREA = 'hazard-usage-by-area',
    // Prep Check Insights
    PREP_CHECK_VIEWS = 'prep-check-views',
    // PREP_CHECK_VIEWS__VISITORS = 'prep-check-views--visitors',
    PREP_CHECK_COMPLETIONS = 'prep-check-completions',
    PREP_CHECK_COMPLETIONS__VISITORS = 'prep-check-completions--visitors',
    PREP_CHECK_COMPLETION_RATE = 'prep-check-completion-rate',
    TRUST_IN_911 = 'trust-in-911',
    TRUST_IN_911__VISITORS = 'trust-in-911--visitors',
    PREP_CHECK_METRICS_BY_AREA = 'prep-check-metrics-by-area',
    PREP_CHECKS_COMPLETED = 'prep-checks-completed',
    MOST_POPULAR_PREP_CHECK = 'most-popular-prep-check',
    EMERGENCY_ITEM_INVENTORY = 'emergency-item-inventory',
    // Individual Prep Check Insights
    INDV_PREP_CHECK_VIEWS = 'indv-prep-check-views',
    // INDV_PREP_CHECK_VIEWS__VISITORS = 'indv-prep-check-views--visitors',
    // INDV_PREP_CHECK_USAGE = 'indv-prep-check-usage',
    // INDV_PREP_CHECK_USAGE__VISITORS = 'indv-prep-check-usage--visitors',
    PREP_CHECK_SCORE = 'prep-check-score',
    PREP_CHECK_SCORE__VISITORS = 'prep-check-score--visitors',
    PREP_CHECK_QUESTION_SCORE = 'prep-check-question-score',
    PREP_CHECK_QUESTION_SCORE__VISITORS = 'prep-check-question-score--visitors',
    PREP_CHECK_SCORE_BY_AREA = 'prep-check-score-by-area',
    PREP_CHECK_USAGE_BY_AREA = 'prep-check-usage-by-area',
    PREP_CHECK_INDV_QUESTION_SCORE = 'prep-check-indv-question-score',
    PREP_CHECK_INDV_QUESTION_SCORE__VISITORS = 'prep-check-indv-question-score--visitors',
    PREP_CHECK_QUESTION_SCORE_BY_AREA = 'prep-check-question-score-by-area',
    PREP_CHECK_Q3_NETWORK_SIZE_DISTRIBUTION = 'q3-network-size-distribution',
    PREP_CHECK_Q3_NETWORK_SIZE_DISTRIBUTION__VISITORS = 'q3-network-size-distribution--visitors',
    PREP_CHECK_Q3_USAGE_OF_TOP_CONTACTS = 'q3-usage-of-top-contacts',
    PREP_CHECK_Q3_USAGE_OF_TOP_CONTACTS__VISITORS = 'q3-usage-of-top-contacts--visitors',
    // Individual Prep Check Question Insights
    PREP_CHECK_Q1_ANSWER_PHASE_ACCURACY = 'q1-answer-phase-accuracy',
    PREP_CHECK_Q1_ANSWER_PHASE_ACCURACY__VISITORS = 'q1-answer-phase-accuracy--visitors',
    PREP_CHECK_Q2_ITEM_INVENTORY = 'q2-item-inventory',
    PREP_CHECK_Q2_ITEM_INVENTORY__VISITORS = 'q2-item-inventory--visitors',
    PREP_CHECK_Q3_CONTACT_USAGE = 'q3-contact-usage',
    PREP_CHECK_Q3_CONTACT_USAGE__VISITORS = 'q3-contact-usage--visitors',
}

export const ZOOM_THRESHOLDS: Record<MapAreaLevel, number> = {
    [MapAreaLevel.STATE]: 2,
    [MapAreaLevel.COUNTY]: 5,
    [MapAreaLevel.ZIP]: 9,
}

export const getMapSpecificityLevel = (zoom = 1): MapAreaLevel => {
    let level: MapAreaLevel = MapAreaLevel.ZIP // ZIP is zoom >= 9
    if (zoom < ZOOM_THRESHOLDS[MapAreaLevel.COUNTY]) {
        level = MapAreaLevel.STATE // STATE is zoom < 5
    } else if (zoom < ZOOM_THRESHOLDS[MapAreaLevel.ZIP]) {
        level = MapAreaLevel.COUNTY // ZIP is zoom >= 5 and zoom < 9
    }
    return level
}
